import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Modal } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../../contexts/ThemeContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { FONTS } from '../../../constants/config';
import { createShadow } from '../../../utils/styles';

interface ActivityHeaderProps {
  title: string;
  dateRange: string;
  description: string;
  weight: number;
  location: string;
  entityName?: string;
  onEdit?: () => void;
  onDelete?: () => void;
}

export default function ActivityHeader({ title, dateRange, description, weight, location, entityName, onEdit, onDelete }: ActivityHeaderProps) {
  const { colors } = useTheme();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDeleteConfirm = () => {
    setShowDeleteModal(false);
    onDelete?.();
  };

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: colors.card,
          borderColor: colors.cardBorder,
        },
        createShadow(0, 1, 3, '#000', 0.1),
      ]}
    >
      {/* Action buttons — hidden for strategy team (entityName present) */}
      {!entityName && (
        <View style={styles.actionButtons}>
          <TouchableOpacity
            style={styles.deleteBtn}
            onPress={() => setShowDeleteModal(true)}
            activeOpacity={0.7}
          >
            <Ionicons name="trash-outline" size={15} color="#FFFFFF" />
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.editBtn, { borderColor: colors.cardBorder, backgroundColor: 'rgba(51,65,85,0.05)' }]}
            onPress={onEdit}
            activeOpacity={0.7}
          >
            <Ionicons name="create-outline" size={15} color={colors.textPrimary} />
          </TouchableOpacity>
        </View>
      )}

      {/* Delete confirmation modal */}
      <Modal
        visible={showDeleteModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowDeleteModal(false)}
      >
        <View style={modalStyles.overlay}>
          <View style={[modalStyles.card, { backgroundColor: colors.card }]}>
            <View style={modalStyles.iconCircle}>
              <Ionicons name="warning-outline" size={32} color="#DC2626" />
            </View>
            <Text style={[modalStyles.title, { color: colors.textPrimary }]}>
              {isRTL ? 'هل أنت متأكد من الحذف؟' : 'Confirm Delete'}
            </Text>
            <Text style={[modalStyles.message, { color: colors.textMuted }]}>
              {isRTL
                ? `سيتم حذف النشاط "${title}" نهائياً ولا يمكن التراجع عن هذا الإجراء.`
                : `Activity "${title}" will be permanently deleted. This action cannot be undone.`}
            </Text>
            <View style={modalStyles.buttons}>
              <TouchableOpacity
                style={modalStyles.cancelBtn}
                onPress={() => setShowDeleteModal(false)}
              >
                <Text style={[modalStyles.cancelText, { color: colors.textMuted }]}>
                  {isRTL ? 'إلغاء' : 'Cancel'}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={modalStyles.deleteConfirmBtn}
                onPress={handleDeleteConfirm}
              >
                <Text style={modalStyles.deleteConfirmText}>
                  {isRTL ? 'حذف' : 'Delete'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Content */}
      <View style={styles.content}>
        <Text style={[styles.title, { color: colors.textPrimary, textAlign: isRTL ? 'right' : 'left' }]}>
          {title}
        </Text>
        {description ? (
          <Text style={[styles.description, { color: '#64748B', textAlign: isRTL ? 'right' : 'left' }]}>
            {description}
          </Text>
        ) : null}

        {/* Metadata badges row */}
        {(dateRange || location || weight > 0) ? (
          <View style={styles.badgesRow}>
            {weight > 0 && (
              <View style={styles.badge}>
                <Text style={[styles.badgeText, { color: colors.textPrimary }]}>{weight}%</Text>
                <Ionicons name="scale-outline" size={20} color={colors.textPrimary} />
              </View>
            )}
            {location ? (
              <View style={styles.badge}>
                <Text style={[styles.badgeText, { color: colors.textPrimary }]}>{location}</Text>
                <Ionicons name="location-outline" size={20} color={colors.textPrimary} />
              </View>
            ) : null}
            {dateRange ? (
              <View style={styles.badge}>
                <Text style={[styles.badgeText, { color: colors.textPrimary }]}>{isRTL && dateRange.includes(' - ') ? dateRange.split(' - ').reverse().join(' - ') : dateRange}</Text>
                <Ionicons name="calendar-outline" size={20} color={colors.textPrimary} />
              </View>
            ) : null}
          </View>
        ) : null}

        {/* Entity tag */}
        {entityName ? (
          <View style={[styles.tagRow, { justifyContent: isRTL ? 'flex-end' : 'flex-start' }]}>
            <View style={styles.entityTag}>
              <Text style={styles.entityTagText}>{entityName}</Text>
            </View>
          </View>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 8,
    borderWidth: 1,
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    justifyContent: 'flex-end',
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  deleteBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: '#DC2626',
    alignItems: 'center',
    justifyContent: 'center',
  },
  editBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flex: 1,
    gap: 8,
    alignItems: 'flex-end',
  },
  title: {
    fontSize: 20,
    fontFamily: FONTS.bold,
    lineHeight: 26,
  },
  description: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    lineHeight: 24,
  },
  badgesRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 20,
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: '#F1F5F9',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  badgeText: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    lineHeight: 24,
  },
  tagRow: {
    flexDirection: 'row',
    marginTop: 4,
  },
  entityTag: {
    backgroundColor: '#A6813F',
    borderRadius: 40,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  entityTagText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#FFFFFF',
  },
});

const modalStyles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    width: 400,
    borderRadius: 12,
    padding: 24,
    alignItems: 'center',
    gap: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 10,
  },
  iconCircle: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#FEE2E2',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: 18,
    fontFamily: FONTS.bold,
    textAlign: 'center',
  },
  message: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'center',
    lineHeight: 22,
  },
  buttons: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
    width: '100%',
  },
  cancelBtn: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 24,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
  },
  cancelText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
  },
  deleteConfirmBtn: {
    flex: 1,
    backgroundColor: '#DC2626',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 24,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
  },
  deleteConfirmText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
});
